import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ComparableUnit, ReferenceUnit, ProjectDetails } from '../super-picker/services/api';
import HuttonsWhiteLogo from '../img/Huttons_white.png';
import { ensurePdfUnicodeFont, PDF_UNICODE_FONT } from './pdfUnicodeFont';

const CORPORATE_COLOR_RGB: [number, number, number] = [26, 35, 126];
const ORANGE_RGB: [number, number, number] = [255, 152, 0];
const DARK_BG_RGB: [number, number, number] = [15, 23, 42];
const FOOTER_BG_RGB: [number, number, number] = [20, 30, 48];
const INFO_BOX_BG_RGB: [number, number, number] = [30, 41, 59];

interface UserInfo {
  Name?: string;
  Mobile?: string;
  Email?: string;
  CEANumber?: string;
  Photo?: string;
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null || isNaN(value) || value === 0) return '-';
  return `$${value.toLocaleString('en-SG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatNumber = (value: number | null | undefined): string => {
  if (value == null || isNaN(value)) return '-';
  return value.toLocaleString('en-SG', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const timeout = setTimeout(() => reject(new Error('timeout')), 10000);
    img.onload = () => { clearTimeout(timeout); resolve(img); };
    img.onerror = () => { clearTimeout(timeout); reject(new Error('failed')); };
    img.src = url;
  });
};

const loadUserPhoto = async (
  doc: jsPDF, userInfo: UserInfo,
  photoX: number, photoY: number, photoRadius: number,
): Promise<boolean> => {
  if (!userInfo.Photo) return false;
  try {
    const photoPath = userInfo.Photo.startsWith('/') ? userInfo.Photo.substring(1) : userInfo.Photo;
    const photoUrl = `https://portal.huttonsgroup.com/${photoPath}`;
    const img = await loadImage(photoUrl);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    const size = photoRadius * 2;
    const px = Math.max(256, Math.round(size * 12));
    canvas.width = px;
    canvas.height = px;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    const r = px / 2;
    const maxInCircle = r * Math.SQRT2;
    const scale = Math.min(maxInCircle / img.width, maxInCircle / img.height);
    const sw = Math.round(img.width * scale);
    const sh = Math.round(img.height * scale);
    const sx = Math.round((px - sw) / 2);
    const sy = Math.round((px - sh) / 2);
    ctx.save();
    ctx.beginPath();
    ctx.arc(r, r, r, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(img, sx, sy, sw, sh);
    ctx.restore();
    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', photoX - photoRadius, photoY - photoRadius, size, size);
    doc.setDrawColor(...ORANGE_RGB);
    doc.setLineWidth(1.5);
    doc.circle(photoX, photoY, photoRadius, 'D');
    return true;
  } catch {
    return false;
  }
};

/** Renders a consistent header with user photo + info on the right */
const renderHeaderWithUser = async (
  doc: jsPDF, title: string, userInfo: UserInfo | null,
): Promise<number> => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const headerHeight = userInfo ? 20 : 12;

  // Orange bar
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(0, 0, pageWidth, 6, 'F');
  // Blue header
  doc.setFillColor(...CORPORATE_COLOR_RGB);
  doc.rect(0, 6, pageWidth, headerHeight, 'F');
  // Title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont(PDF_UNICODE_FONT, 'bold');
  doc.text(title, 10, 6 + headerHeight / 2 + 2);

  if (userInfo) {
    const photoRadius = 7;
    const photoX = pageWidth - 18;
    const photoY = 6 + headerHeight / 2;

    // Text to the left of photo
    const textRightEdge = photoX - photoRadius - 4;
    doc.setFontSize(9);
    doc.setFont(PDF_UNICODE_FONT, 'bold');
    doc.setTextColor(...ORANGE_RGB);
    if (userInfo.Name) doc.text(userInfo.Name, textRightEdge, photoY - 3, { align: 'right' });
    doc.setTextColor(255, 255, 255);
    doc.setFont(PDF_UNICODE_FONT, 'normal');
    doc.setFontSize(7);
    const details: string[] = [];
    if (userInfo.CEANumber) details.push(`CEA: ${userInfo.CEANumber}`);
    if (userInfo.Mobile) details.push(userInfo.Mobile);
    if (details.length > 0) doc.text(details.join('  |  '), textRightEdge, photoY + 3, { align: 'right' });

    // Photo circle
    const photoLoaded = await loadUserPhoto(doc, userInfo, photoX, photoY, photoRadius);
    if (!photoLoaded) {
      doc.setFillColor(...ORANGE_RGB);
      doc.circle(photoX, photoY, photoRadius, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont(PDF_UNICODE_FONT, 'bold');
      const initials = userInfo.Name ? userInfo.Name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'U';
      doc.text(initials, photoX, photoY + 1.5, { align: 'center' });
    }
  }

  return 6 + headerHeight + 4;
};

export const generateUnitFloorPlanPdf = async (
  unit: ComparableUnit | ReferenceUnit,
  projectDetails: ProjectDetails | null,
  userInfo: UserInfo | null,
  isEc: boolean,
): Promise<void> => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  await ensurePdfUnicodeFont(doc);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // ── Header with user photo (consistent with comparison PDF) ──
  let yPos = await renderHeaderWithUser(doc, `FLOOR PLAN - ${unit.projectName} ${unit.unitNumber}`, userInfo);

  // ── Project Info ──
  if (projectDetails) {
    doc.setTextColor(...CORPORATE_COLOR_RGB);
    doc.setFontSize(14);
    doc.setFont(PDF_UNICODE_FONT, 'bold');
    doc.text(projectDetails.name || unit.projectName, 10, yPos + 5);
    yPos += 10;

    doc.setTextColor(100, 100, 100);
    doc.setFontSize(8);
    doc.setFont(PDF_UNICODE_FONT, 'normal');
    const infoLine: string[] = [];
    if (projectDetails.developer) infoLine.push(projectDetails.developer);
    if (projectDetails.tenure) infoLine.push(projectDetails.tenure);
    if (projectDetails.district) infoLine.push(projectDetails.district);
    if (projectDetails.area) infoLine.push(projectDetails.area);
    if (infoLine.length > 0) {
      doc.text(infoLine.join('  |  '), 10, yPos);
      yPos += 5;
    }
    if (projectDetails.address) {
      doc.text(projectDetails.address, 10, yPos);
      yPos += 5;
    }
  } else {
    doc.setTextColor(...CORPORATE_COLOR_RGB);
    doc.setFontSize(14);
    doc.setFont(PDF_UNICODE_FONT, 'bold');
    doc.text(unit.projectName, 10, yPos + 5);
    yPos += 12;
  }

  yPos += 4;

  // ── Floor plan image ──
  const floorPlanUrl = unit.floorplan;
  const imgMargin = 15;
  const imgMaxWidth = pageWidth - imgMargin * 2;
  const imgMaxHeight = 130;

  if (floorPlanUrl) {
    try {
      const img = await loadImage(floorPlanUrl);
      const aspectRatio = img.width / img.height;
      let drawWidth = imgMaxWidth;
      let drawHeight = drawWidth / aspectRatio;
      if (drawHeight > imgMaxHeight) {
        drawHeight = imgMaxHeight;
        drawWidth = drawHeight * aspectRatio;
      }
      const imgX = (pageWidth - drawWidth) / 2;

      // Light border around image
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.roundedRect(imgX - 3, yPos - 2, drawWidth + 6, drawHeight + 4, 2, 2, 'S');

      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        doc.addImage(dataUrl, 'JPEG', imgX, yPos, drawWidth, drawHeight);
      }
      yPos += drawHeight + 8;
    } catch {
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(10);
      doc.text('Floor plan image unavailable', pageWidth / 2, yPos + 20, { align: 'center' });
      yPos += 40;
    }
  } else {
    yPos += 5;
  }

  // ── Unit Info Table ──
  const npsPrice = unit.priceNps ?? unit.price ?? 0;
  const dpsPrice = (unit as any).priceDps ?? 0;
  const npsPsf = unit.psfNps ?? unit.psf ?? 0;
  const dpsPsf = (unit as any).psfDps ?? 0;

  const bedroomType = (unit as any).unitTypeDescription || (unit as any).unitType || '';
  const bedroomDisplay = unit.bedroom != null ? String(unit.bedroom) : '-';

  const unitRows: string[][] = [
    ['Unit', unit.unitNumber],
    ['Block', unit.blockNumber || '-'],
    ['Bedroom', bedroomDisplay],
    ...(bedroomType ? [['Bedroom Type', bedroomType]] : []),
    ['Bathroom', String(unit.bathroom ?? '-')],
    ['Area', unit.areaSqft ? `${formatNumber(unit.areaSqft)} sqft` : '-'],
  ];

  if (unit.unitStatus?.toString().toLowerCase() !== 'sold') {
    if (isEc) {
      unitRows.push(['NPS', npsPrice > 0 ? formatCurrency(npsPrice) : '-']);
      unitRows.push(['NPS PSF', npsPsf > 0 ? formatCurrency(npsPsf) : '-']);
      unitRows.push(['DPS', dpsPrice > 0 ? formatCurrency(dpsPrice) : '-']);
      unitRows.push(['DPS PSF', dpsPsf > 0 ? formatCurrency(dpsPsf) : '-']);
    } else {
      unitRows.push(['Price', npsPrice > 0 ? formatCurrency(npsPrice) : '-']);
      unitRows.push(['PSF', npsPsf > 0 ? formatCurrency(npsPsf) : '-']);
    }
  }

  // Section header
  doc.setFillColor(...CORPORATE_COLOR_RGB);
  doc.roundedRect(10, yPos, pageWidth - 20, 8, 1, 1, 'F');
  doc.setFontSize(10);
  doc.setFont(PDF_UNICODE_FONT, 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('UNIT INFORMATION', pageWidth / 2, yPos + 5.5, { align: 'center' });
  yPos += 10;

  autoTable(doc, {
    startY: yPos,
    body: unitRows,
    theme: 'grid',
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 40, textColor: [100, 100, 100] as [number, number, number], fillColor: [245, 247, 250] },
      1: { fontStyle: 'bold', textColor: [0, 0, 0] as [number, number, number] },
    },
    styles: {
      font: PDF_UNICODE_FONT,
      fontSize: 9,
      cellPadding: { top: 3, bottom: 3, left: 10, right: 10 },
      textColor: [0, 0, 0],
    },
    margin: { left: 10, right: 10 },
  });

  // ── Footer ──
  const footerY = pageHeight - 14;
  doc.setFillColor(...FOOTER_BG_RGB);
  doc.rect(0, footerY, pageWidth, 14, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  doc.setFont(PDF_UNICODE_FONT, 'normal');
  // Huttons logo
  try {
    const logoImg = await loadImage(HuttonsWhiteLogo);
    const logoHeight = 7;
    const logoWidth = logoHeight * (logoImg.width / logoImg.height);
    doc.addImage(HuttonsWhiteLogo, 'PNG', 10, footerY + (14 - logoHeight) / 2, logoWidth, logoHeight);
  } catch {
    doc.text('Huttons', 10, footerY + 7);
  }
  const now = new Date();
  const genDate = now.toLocaleDateString('en-SG', { year: 'numeric', month: 'long', day: 'numeric' });
  const genTime = now.toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  doc.text(`Generated on ${genDate} ${genTime}`, pageWidth / 2, footerY + 7, { align: 'center' });
  if (userInfo) {
    doc.setTextColor(...ORANGE_RGB);
    doc.setFont(PDF_UNICODE_FONT, 'bold');
    if (userInfo.Name) doc.text(userInfo.Name, pageWidth - 10, footerY + 5, { align: 'right' });
    doc.setTextColor(255, 255, 255);
    doc.setFont(PDF_UNICODE_FONT, 'normal');
    if (userInfo.CEANumber) doc.text(`CEA: ${userInfo.CEANumber}`, pageWidth - 10, footerY + 10, { align: 'right' });
  }

  // ── Save ──
  const unitName = unit.unitNumber.replace(/[^a-zA-Z0-9-]/g, '');
  const projectName = unit.projectName.replace(/\s+/g, '_');
  const fileName = `Floor_Plan_${projectName}_${unitName}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};
